<template>
  <div
    class="sf-catnav"
    :class="[`sf-catnav--${variant}`, { 'sf-catnav--sticky': sticky }]"
    :style="sticky ? { top: `${top}px` } : undefined"
  >
    <div ref="scroller" class="sf-catnav__scroller" role="tablist" aria-label="Categorias">
      <button
        v-for="item in items"
        :key="item.id"
        type="button"
        role="tab"
        class="sf-catnav__item"
        :class="{ 'is-active': String(item.id) === String(activeId) }"
        :aria-selected="String(item.id) === String(activeId)"
        :data-catnav-id="item.id"
        @click="emit('select', String(item.id))"
      >
        {{ item.name }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { nextTick, ref, watch } from 'vue'

/* global defineProps, defineEmits, withDefaults */
const props = withDefaults(defineProps<{
  items: { id: string | number; name: string }[]
  activeId: string | null
  /** chips = pílulas; underline = abas com sublinhado (estilo Delivery / abas) */
  variant?: 'chips' | 'underline'
  sticky?: boolean
  /** px from the top of the viewport (fixed header height) */
  top?: number
}>(), { variant: 'chips', sticky: false, top: 0 })

const emit = defineEmits(['select'])

const scroller = ref<HTMLElement | null>(null)

// Keeps the active item visible inside the horizontal bar (without scrolling the page)
watch(() => props.activeId, async (id) => {
  await nextTick()
  const bar = scroller.value
  const button = bar?.querySelector<HTMLElement>(`[data-catnav-id="${CSS.escape(String(id))}"]`)
  if (!bar || !button) return
  const left = button.offsetLeft - (bar.clientWidth - button.offsetWidth) / 2
  bar.scrollTo({ left: Math.max(0, left), behavior: 'smooth' })
})
</script>

<style>
.sf-catnav {
  z-index: 9;
  background: var(--sf-bg);
  margin: 0 0 1.25rem;
}
.sf-catnav--sticky {
  position: sticky;
}
.sf-catnav__scroller {
  display: flex;
  gap: .5rem;
  overflow-x: auto;
  scrollbar-width: none;
  padding: .625rem 0;
}
.sf-catnav__scroller::-webkit-scrollbar {
  display: none;
}
@media (max-width: 1024px) {
  /* the menu has 1rem side padding on small screens: let the bar bleed to the edges */
  .sf-catnav {
    margin-left: -1rem;
    margin-right: -1rem;
  }
  .sf-catnav__scroller {
    padding-left: 1rem;
    padding-right: 1rem;
  }
}
.sf-catnav__item {
  flex-shrink: 0;
  white-space: nowrap;
  font-family: var(--sf-font);
  font-size: .875rem;
  font-weight: 600;
  color: var(--sf-text);
  transition: color .2s, background-color .2s, border-color .2s;
}

/* chips */
.sf-catnav--chips .sf-catnav__item {
  padding: .45rem 1rem;
  border: 1px solid var(--sf-border);
  border-radius: 999px;
  background: var(--sf-card-bg);
}
.sf-catnav--chips .sf-catnav__item:hover {
  border-color: var(--sf-primary);
}
.sf-catnav--chips .sf-catnav__item.is-active {
  background: var(--sf-primary);
  border-color: var(--sf-primary);
  color: var(--sf-on-primary);
}

/* underline (Delivery / abas) */
.sf-catnav--underline {
  border-bottom: 1px solid var(--sf-border);
}
.sf-catnav--underline .sf-catnav__scroller {
  gap: 1.5rem;
  padding-top: .25rem;
  padding-bottom: 0;
}
.sf-catnav--underline .sf-catnav__item {
  position: relative;
  padding: .75rem 0;
  color: var(--sf-muted);
}
.sf-catnav--underline .sf-catnav__item:hover,
.sf-catnav--underline .sf-catnav__item.is-active {
  color: var(--sf-text);
}
.sf-catnav--underline .sf-catnav__item::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 3px;
  border-radius: 3px 3px 0 0;
  background: var(--sf-primary);
  transform: scaleX(0);
  transition: transform .2s ease;
}
.sf-catnav--underline .sf-catnav__item.is-active::after {
  transform: scaleX(1);
}
.sf-catnav--sticky.sf-catnav--chips {
  box-shadow: 0 1px 0 var(--sf-border);
}
</style>
